import React, { useEffect, useState } from 'react';
import { ILegalTransactionGwGFullObject } from '../../../Interfaces/ILegalTransactionGwG';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import { CustomeTextField } from '../../../generic/CustomeTextField';
import { Delete, Edit } from '@mui/icons-material';
import { GwGEntry } from './GwGEntry';
import {
  createNewGwGEntry,
  ILegalTransactionGwGEntry,
} from '../../../Interfaces/ILegalTransactionGwGEntry';
import { GwGEntryDialog } from './GwGEntryDialog';
import { useGetSystemMail } from '../../../hooks/data';
import { GwGRejectDialog } from './GwGRejectDialog';
import { DialogStructure } from '../../../core/DialogStructure';

interface IProps {
  gwgObject: ILegalTransactionGwGFullObject;
  gwgArray: ILegalTransactionGwGFullObject[];
  setGwgArray: Function;
}

export const GwGElement: React.FC<IProps> = ({
  gwgObject,
  gwgArray,
  setGwgArray,
}) => {
  const [editGwgObject, setEditGwgObject] = useState(gwgObject);
  const [isGwGObjectDialogOpen, setIsGwGObjectDialogOpen] = useState(false);
  const [gwgEntryArray, setGwgEntryArray] = useState(
    gwgObject.LegalTransactionGwGEntryArray
  );

  // Gemeinsame Dialog-States für Add/Edit
  const [activeEntry, setActiveEntry] =
    useState<ILegalTransactionGwGEntry | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(null);
  const [entryType, setEntryType] = useState<'payment' | 'demand' | null>(null);

  // Reject Dialog + Mailtexte
  const [isOpenReject, setIsOpenReject] = useState(false);
  const [rejectSubject, setRejectSubject] = useState('');
  const [rejectBody, setRejectBody] = useState('');
  const [activeRejectEntry, setActiveRejectEntry] =
    useState<ILegalTransactionGwGEntry | null>(null);

  const { systemMail: rejectTemplate } = useGetSystemMail(41);
  const { systemMail: acceptTemplate } = useGetSystemMail(42);

  // Synchronisierung ins Parent-Array
  useEffect(() => {
    setGwgArray([
      ...gwgArray.map(x =>
        x.idLegalTransactionGwG === editGwgObject.idLegalTransactionGwG
          ? { ...editGwgObject, LegalTransactionGwGEntryArray: gwgEntryArray }
          : x
      ),
    ]);
  }, [gwgEntryArray]);

  useEffect(() => {
    handleGwGSave();
  }, [editGwgObject.total]);

  const handleGwGSave = () => {
    setGwgArray((prev: ILegalTransactionGwGFullObject[]) =>
      prev.map(x =>
        x.idLegalTransactionGwG === editGwgObject.idLegalTransactionGwG
          ? { ...editGwgObject }
          : x
      )
    );

    setIsGwGObjectDialogOpen(false);
  };
  const handleOpenReject = (entry: ILegalTransactionGwGEntry) => {
    setActiveRejectEntry(entry);
    setRejectSubject(entry.mailSubject || rejectTemplate?.Subject || '');
    setRejectBody(entry.mailBody || rejectTemplate?.Body || '');
    setIsOpenReject(true);
  };
  const handleRejectSave = () => {
    if (!activeRejectEntry) return;
    setGwgEntryArray(prev =>
      prev.map(e =>
        e.idLegalTransactionGwGEntry ===
        activeRejectEntry.idLegalTransactionGwGEntry
          ? {
              ...e,
              idLegalTransactionGwGEntryState: 3,
              mailSubject: rejectSubject,
              mailBody: rejectBody,
            }
          : e
      )
    );
    setIsOpenReject(false);
  };
  const handleAccept = (entry: ILegalTransactionGwGEntry) => {
    setGwgEntryArray(prev =>
      prev.map(e =>
        e.idLegalTransactionGwGEntry === entry.idLegalTransactionGwGEntry
          ? {
              ...e,
              idLegalTransactionGwGEntryState: 2,
              mailSubject: acceptTemplate?.Subject || '',
              mailBody: acceptTemplate?.Body || '',
            }
          : e
      )
    );
  };

  const openAddDialog = (isPaid: boolean) => {
    let minId = -1;
    if (gwgEntryArray.length > 0) {
      const minIdArray =
        Math.min(...gwgEntryArray.map(x => x.idLegalTransactionGwGEntry)) - 1;
      if (minIdArray <= minId) {
        minId = minIdArray - 1;
      }
    }

    const newEntry = createNewGwGEntry(
      isPaid,
      editGwgObject.idLegalTransactionGwG,
      minId
    );

    setActiveEntry(newEntry);
    setEntryType(newEntry.isPaid ? 'payment' : 'demand');
    setDialogMode('add');
    setIsDialogOpen(true);
  };
  const handleEdit = (entry: ILegalTransactionGwGEntry) => {
    setActiveEntry(entry);
    setEntryType(entry.isPaid ? 'payment' : 'demand');
    setDialogMode('edit');
    setIsDialogOpen(true);
  };
  const handleDelete = () => {
    const tempArray = gwgArray.filter(
      x => x.idLegalTransactionGwG !== editGwgObject.idLegalTransactionGwG
    );
    setGwgArray(tempArray);
  };

  const paidSum = gwgEntryArray
    .filter(x => x.wasPaid && x.idLegalTransactionGwGEntryState === 2)
    .map(x => x.currentValue)
    .reduce((a, b) => a + b, 0);
  const getSumWasPaid = () => {
    return paidSum.toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
    });
  };
  const prozPaidSum = (paidSum / Number(editGwgObject.total)) * 100;

  const getSumIsNotPaid = (asStrin: boolean = true) => {
    const wasPaidSum = gwgEntryArray
      .filter(x => !x.isPaid)
      .map(x => x.currentValue + (x.currentRate ?? 0) + (x.extraCosts ?? 0))
      .reduce((a, b) => a + b, 0);

    if (asStrin) {
      return wasPaidSum.toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
      });
    } else {
      return wasPaidSum;
    }
  };
  const getDiffIsNotPaid = () => {
    const wasPaidSum = gwgEntryArray
      .filter(x => !x.isPaid)
      .map(x => x.currentValue + (x.currentRate ?? 0) + (x.extraCosts ?? 0))
      .reduce((a, b) => a + b, 0);
    const wasPaidSumPaid = gwgEntryArray
      .filter(x => !x.isPaid)
      .filter(x => x.wasPaid)
      .map(x => x.currentValue + (x.currentRate ?? 0) + (x.extraCosts ?? 0))
      .reduce((a, b) => a + b, 0);
    return (wasPaidSum - wasPaidSumPaid).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
    });
  };
  const getDiffPaid = () => {
    const paidSum = gwgEntryArray
      .filter(x => x.isPaid)
      .map(x => x.currentValue)
      .reduce((a, b) => a + b, 0);
    const wasPaidSum = gwgEntryArray
      .filter(x => !x.isPaid)
      .filter(x => x.wasPaid)
      .map(x => x.currentValue + (x.currentRate ?? 0) + (x.extraCosts ?? 0))
      .reduce((a, b) => a + b, 0);
    return (editGwgObject.total - paidSum - wasPaidSum).toLocaleString(
      'de-DE',
      { style: 'currency', currency: 'EUR' }
    );
  };
  const getBackgroundColor = () => {
    const paidSum = gwgEntryArray
      .filter(x => x.wasPaid)
      .map(x => x.currentValue)
      .reduce((a, b) => a + b, 0);

    if (paidSum > editGwgObject.total) {
      return '#ffe0d4';
    } else {
      // Berechnung der Nähe
      const proximity = Math.abs(editGwgObject.total - paidSum);
      // Maximalwert für die Differenz (du kannst dies anpassen)
      const maxProximity = editGwgObject.total;

      // Berechnung der Transparenz (zwischen 0 und 1)
      const alpha = Math.max(0, 1 - proximity / maxProximity);

      // Rückgabe der Hintergrundfarbe in rgb-Format
      return `rgb(183, 213, 172, ${alpha})`;
    }
  };

  return (
    <>
      <DialogStructure
        open={isGwGObjectDialogOpen}
        title='Zahlungsübersicht'
        onSave={handleGwGSave}
        onCancel={() => {
          setEditGwgObject(gwgObject);
          setIsGwGObjectDialogOpen(false);
        }}
        titleVariant='h6'
        maxWidth='sm'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomeTextField
              label='Titel'
              attr='title'
              object={editGwgObject}
              setObject={setEditGwgObject}
              type='string'
              required
            />
          </Grid>
        </Grid>
      </DialogStructure>

      <GwGEntryDialog
        open={isDialogOpen}
        entry={activeEntry}
        setEntry={setActiveEntry}
        entryType={entryType}
        isEditMode={dialogMode === 'edit'}
        onSave={() => {
          if (!activeEntry) return;
          setGwgEntryArray(prev => {
            if (dialogMode === 'add') return [...prev, activeEntry];
            if (dialogMode === 'edit')
              return prev.map(e =>
                e.idLegalTransactionGwGEntry ===
                activeEntry.idLegalTransactionGwGEntry
                  ? activeEntry
                  : e
              );
            return prev;
          });
          setIsDialogOpen(false);
        }}
        onCancel={() => {
          setIsDialogOpen(false);
        }}
        documentArray={activeEntry?.DocumentArray || []}
        setDocumentArray={arr =>
          setActiveEntry(prev =>
            prev ? { ...prev, DocumentArray: arr } : prev
          )
        }
      />

      <GwGRejectDialog
        open={isOpenReject}
        subject={rejectSubject}
        body={rejectBody}
        setSubject={setRejectSubject}
        setBody={setRejectBody}
        onSave={handleRejectSave}
        onCancel={() => setIsOpenReject(false)}
      />

      <Card variant='outlined'>
        <CardContent>
          <Typography
            variant='h6'
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {editGwgObject.title}

            <IconButton
              size='small'
              onClick={() => setIsGwGObjectDialogOpen(true)}
              sx={{ ml: 0.5 }}
            >
              <Edit />
            </IconButton>

            <IconButton sx={{ mb: 1, ml: 'auto' }} onClick={handleDelete}>
              <Delete />
            </IconButton>
          </Typography>

          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Button
                variant='outlined'
                sx={{ mb: 1 }}
                onClick={() => openAddDialog(true)}
              >
                Neue Zahlung
              </Button>
            </Grid>
            <Grid item sm={6}>
              <Button
                variant='outlined'
                sx={{ mb: 1, float: 'right' }}
                onClick={() => openAddDialog(false)}
              >
                Neue Ablöseforderung
              </Button>
            </Grid>

            <Grid item sm={12}>
              <CustomeTextField
                label='Gesamtsumme / Kaufpreis'
                attr='total'
                object={editGwgObject}
                setObject={setEditGwgObject}
                type='euro'
              />
            </Grid>

            <Grid item sm={6}>
              <Typography
                sx={{
                  mt: 1,
                  p: 1,
                  m: 1,
                }}
              >
                Gezahlt: {getSumWasPaid()} von{' '}
                {editGwgObject.total.toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                })}
                <br />
                übrig: {getDiffPaid()}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress
                      variant='determinate'
                      value={prozPaidSum > 100 ? 100 : prozPaidSum}
                      color={
                        prozPaidSum < 50
                          ? 'error'
                          : prozPaidSum < 95
                            ? 'warning'
                            : prozPaidSum > 105
                              ? 'info'
                              : 'success'
                      }
                    />
                  </Box>
                  <Box sx={{ minWidth: 35, ml: 1 }}>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary' }}
                    >
                      {`${Math.round(prozPaidSum)}%`}
                    </Typography>
                  </Box>
                </Box>
                Anteil an Verkäufer:{' '}
                {(
                  editGwgObject.total - Number(getSumIsNotPaid(false))
                ).toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                })}
              </Typography>
            </Grid>

            <Grid item sm={6} sx={{ borderLeft: 1 }}>
              <Typography sx={{ mt: 1, p: 1, m: 1 }}>
                Ablöseforderung: {getSumIsNotPaid()}
                <br />
                davon offen: {getDiffIsNotPaid()}
              </Typography>
            </Grid>

            <Grid item sm={6} sx={{ borderTop: 1 }}>
              <Box sx={{ mr: 1 }}>
                {gwgEntryArray
                  .filter(gwgEntry => gwgEntry.isPaid)
                  .map(gwgEntry => (
                    <GwGEntry
                      key={`gwg-entry-${gwgEntry.idLegalTransactionGwGEntry}`}
                      gwgEntryObject={gwgEntry}
                      gwgEntryArray={gwgEntryArray}
                      setGwgEntryArray={setGwgEntryArray}
                      onEdit={handleEdit}
                      onReject={handleOpenReject}
                      onAccept={handleAccept}
                    />
                  ))}
              </Box>
            </Grid>

            <Grid item sm={6} sx={{ borderTop: 1, borderLeft: 1 }}>
              {gwgEntryArray
                .filter(gwgEntry => !gwgEntry.isPaid)
                .map(gwgEntry => (
                  <GwGEntry
                    key={`gwg-entry-${gwgEntry.idLegalTransactionGwGEntry}`}
                    gwgEntryObject={gwgEntry}
                    gwgEntryArray={gwgEntryArray}
                    setGwgEntryArray={setGwgEntryArray}
                    onEdit={handleEdit}
                    onReject={handleOpenReject}
                    onAccept={handleAccept}
                  />
                ))}
            </Grid>
            <Grid item sm={12} sx={{ borderTop: 1, mt: 1 }} />
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};
